import React from 'react';
import { Card, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    FaUser,
    FaMapMarkerAlt,
    FaEuroSign,
    FaFileContract,
    FaFileAlt,
    FaCheckCircle,
    FaExclamationCircle,
    FaCircle,
    FaCreditCard,
    FaBatteryFull
} from 'react-icons/fa';
import './OfferProgressTimeline.css';

const STAGE_ICONS = {
    resumen: FaFileContract,
    tarifa: FaEuroSign,
    cliente: FaUser,
    pagador: FaCreditCard,
    cups: FaMapMarkerAlt,
    bateria_virtual: FaBatteryFull,
    documentacion: FaFileAlt
};

const STAGE_ROUTES = {
    resumen: 'resumen',
    tarifa: 'tarifas',
    cliente: 'partner',
    pagador: 'pagador',
    cups: 'cups',
    bateria_virtual: 'bateria-virtual',
    documentacion: 'documentacion'
};

const FIELD_LABELS = {
    tarifa_oferta: 'Tarifa de Acceso',
    tarifa_oferta_gas: 'Tarifa de Acceso Gas',
    sub_type_offer: 'Subtipo de Oferta',
    mode_facturacio: 'Modo de Facturación',
    fecha_estimada_inicio_ctto: 'Fecha Inicio',
    cliente_nombre: 'Nombre',
    cliente_vat: 'NIF/CIF',
    cliente_email_1: 'Email',
    cliente_telefono_1: 'Teléfono',
    pagador_autorizacion_sepa: 'Autorización SEPA',
    iban: 'IBAN',
    cups_name: 'CUPS',
    cups_nv: 'Calle',
    cups_dp: 'CP',
    cups_id_municipi: 'Municipio',
    cnae: 'CNAE',
    cnae_gas: 'CNAE Gas'
};

// Full Horizontal Stage Component (with all details)
const HorizontalStage = ({ stage, onEdit, isLast, isActive, onClick }) => {
    const IconComponent = STAGE_ICONS[stage.id] || FaCircle;

    const getStatusIcon = () => {
        if (stage.isBlocked) {
            return <FaCircle className="status-icon-h blocked" />;
        }
        if (stage.status === 'completed') {
            return <FaCheckCircle className="status-icon-h completed" />;
        } else if (stage.status === 'in_progress') {
            return <FaExclamationCircle className="status-icon-h in-progress" />;
        }
        return <FaCircle className="status-icon-h pending" />;
    };

    const renderMissingFields = (props) => {
        if (!stage.missing_fields || stage.missing_fields.length === 0) return <></>;

        return (
            <Tooltip id={`tooltip-${stage.id}`} {...props} className="missing-fields-tooltip">
                <div className="text-start">
                    <strong>Falta por completar:</strong>
                    <ul className="ps-3 mb-0 mt-1 small">
                        {stage.missing_fields.map(field => (
                            <li key={field}>{FIELD_LABELS[field] || field}</li>
                        ))}
                    </ul>
                </div>
            </Tooltip>
        );
    };

    const StageContent = (
        <div
            className={`stage-card-h ${isActive ? 'active' : ''} ${stage.isBlocked ? 'blocked' : ''}`}
            onClick={stage.isBlocked ? null : onClick}
            style={{ cursor: stage.isBlocked ? 'not-allowed' : 'pointer', opacity: stage.isBlocked ? 0.6 : 1 }}
            role="button"
            tabIndex={stage.isBlocked ? -1 : 0}
            onKeyDown={(e) => {
                if (!stage.isBlocked && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClick();
                }
            }}
            aria-label={`Navegar a ${stage.name}. ${stage.percentage}% completado${stage.missing_fields && stage.missing_fields.length > 0 ? `. ${stage.missing_fields.length} campos pendientes` : ''}`}
        >
            <div className="stage-indicator-h">
                {getStatusIcon()}
            </div>

            <div className="stage-content-h">
                <div className="stage-header-h">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconComponent className="stage-icon-h" />
                        <h6 className="mb-0 fw-bold">{stage.name}</h6>
                    </div>

                    <div className="stage-progress-h">
                        <ProgressBar
                            now={stage.percentage}
                            variant={
                                stage.percentage >= 100 ? 'success' :
                                    stage.percentage > 0 ? 'warning' :
                                        'secondary'
                            }
                            className="mb-1"
                            style={{ height: '6px' }}
                        />
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">{stage.percentage}% completado</small>
                            {stage.missing_fields && stage.missing_fields.length > 0 && (
                                <small className="text-danger fw-bold" style={{ fontSize: '0.7rem' }}>
                                    {stage.missing_fields.length} pendientes
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="timeline-stage-h">
            {stage.missing_fields && stage.missing_fields.length > 0 ? (
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={renderMissingFields}
                >
                    {StageContent}
                </OverlayTrigger>
            ) : (
                StageContent
            )}

            {!isLast && <div className="connector-arrow-h">→</div>}
        </div>
    );
};

const OfferProgressTimeline = ({ progressData, ofertaId, sectorType, onNavigate, horizontal = false, currentStep, onStepClick, blockedStages = [], showVirtualBattery = true }) => {
    if (!progressData || !progressData.stages) {
        return null; // Don't show anything if no data
    }

    const { completion_percentage, stages } = progressData;

    const handleStageClick = (stageId) => {
        const route = STAGE_ROUTES[stageId] || stageId;
        if (onNavigate) {
            onNavigate(route);
        } else if (onStepClick) {
            onStepClick(stageId);
        }
    };

    if (horizontal) {
        // Correct order of stages
        const ORDERED_STAGE_IDS = ['tarifa', 'cliente', 'pagador', 'cups', 'bateria_virtual', 'documentacion'];

        // Manté ProgressTimeline sincronitzada amb condició visibilitat BV
        const rawStages = (stages ? [...stages] : []).filter((stage) => (
            showVirtualBattery || stage.id !== 'bateria_virtual'
        ));

        const stagesToRender = rawStages.sort((a, b) => {
            const indexA = ORDERED_STAGE_IDS.indexOf(a.id);
            const indexB = ORDERED_STAGE_IDS.indexOf(b.id);

            // If both found in order list, sort by index
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;

            // If only A found, A comes first
            if (indexA !== -1) return -1;

            // If only B found, B comes first
            if (indexB !== -1) return 1;

            return 0; // Keep original order if neither found
        });

        return (
            <Card className="offer-progress-timeline">
                <Card.Body>
                    <div className="offer-progress-timeline-h">
                        <div className="timeline-horizontal-container">
                            {/* Progress Indicator - Clickable to go to Dashboard */}
                            <div
                                className="progress-indicator-wrapper"
                                onClick={() => handleStageClick('resumen')}
                                style={{ cursor: 'pointer' }}
                                title="Ir al Resumen"
                            >
                                <div className="circular-progress">
                                    <svg width="80" height="80" viewBox="0 0 80 80">
                                        <circle cx="40" cy="40" r="36" fill="none" stroke="#e9ecef" strokeWidth="6" />
                                        <circle
                                            cx="40" cy="40" r="36" fill="none"
                                            stroke="var(--ab-primary)" strokeWidth="6"
                                            strokeDasharray={`${2 * Math.PI * 36 * (completion_percentage / 100)} ${2 * Math.PI * 36}`}
                                            strokeLinecap="round" transform="rotate(-90 40 40)"
                                            className="progress-circle"
                                        />
                                    </svg>
                                    <div className="progress-text">
                                        <span className="percentage">{Math.round(completion_percentage)}%</span>
                                    </div>
                                </div>
                                <div className="progress-label">
                                    <small className="text-muted fw-bold">Completado</small>
                                </div>
                            </div>

                            {/* Stages - Horizontal */}
                            <div className="horizontal-stepper">
                                {stagesToRender.map((stage, index) => (
                                    <HorizontalStage
                                        key={stage.id}
                                        stage={{
                                            ...stage,
                                            isBlocked: blockedStages.includes(stage.id)
                                        }}
                                        onEdit={() => handleStageClick(stage.id)}
                                        onClick={() => handleStageClick(stage.id)}
                                        isActive={currentStep === stage.id}
                                        isLast={index === stagesToRender.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // Vertical layout (original)
    return (
        <Card className="offer-progress-timeline">
            <Card.Header className="timeline-header">
                <h5 className="mb-0 fw-bold">Progreso de la Oferta</h5>
                <div className="overall-progress">
                    <div className="circular-progress">
                        <svg width="80" height="80" viewBox="0 0 80 80">
                            <circle cx="40" cy="40" r="36" fill="none" stroke="#e9ecef" strokeWidth="6" />
                            <circle
                                cx="40" cy="40" r="36" fill="none" stroke="var(--ab-primary)" strokeWidth="6"
                                strokeDasharray={`${2 * Math.PI * 36 * (completion_percentage / 100)} ${2 * Math.PI * 36}`}
                                strokeLinecap="round" transform="rotate(-90 40 40)"
                                className="progress-circle"
                            />
                        </svg>
                        <div className="progress-text">
                            <span className="percentage">{Math.round(completion_percentage)}%</span>
                        </div>
                    </div>
                    <div className="progress-label">
                        <small className="text-muted">Completado</small>
                    </div>
                </div>
            </Card.Header>
            <Card.Body>
                <div className="timeline-stages">
                    {/* Vertical stages removed for now, horizontal is primary */}
                </div>
            </Card.Body>
        </Card>
    );
};

export default OfferProgressTimeline;
